<template>
  <div class="sena-edition-games">
    <span id="s-result">
      <GamesResultsAddUpdate
        v-if="showResult"
        @showAdd="closeResult"
        @showMore="refetchAll"
        :page="1"
        :updateData="resultData"/>
    </span>

    <va-card :title="cardTitle">
      <template slot="actions">
        <va-button
          v-if="isDraft"
          icon="fa fa-plus"
          :to="{ name: 'sena-games-pick', params: { edition: edition } }"
        />
      </template>

      <!-- Cabeçalho vivo: é aqui que a equipe vê o efeito de cada placar. -->
      <div class="sena-summary mb-3" v-if="progress">
        <span class="sena-summary__item">
          <b>{{ progress.games_resolved }}/{{ progress.games_total }}</b>
          {{ $t('sena.labels.gamesResolved') }}
        </span>
        <span class="sena-summary__item">
          <b>{{ progress.tickets_alive }}</b>
          {{ $t('sena.labels.of') }} {{ progress.tickets_total }}
          {{ $t('sena.labels.survivors') }}
        </span>
        <span class="sena-summary__item" v-if="progress.games_blocked > 0">
          <va-badge color="danger">{{ $t('sena.labels.willVoid') }}</va-badge>
        </span>
        <span class="sena-summary__item">
          <b>{{ $t('sena.forms.lockoutLabel') }}:</b> {{ lockoutText }}
        </span>
      </div>

      <va-data-table
        :fields="fields"
        :data="games"
        :loading="loading"
        no-data-label="Nenhum jogo selecionado"
      >
        <template slot="confronto" slot-scope="props">
          {{ props.rowData.game.club1.name }} <b>x</b> {{ props.rowData.game.club2.name }}
        </template>

        <template slot="placar" slot-scope="props">
          <span v-if="resultOf(props.rowData)">
            <b>{{ resultOf(props.rowData).pts_club1 }} x {{ resultOf(props.rowData).pts_club2 }}</b>
          </span>
          <span v-else class="sena-muted">{{ $t('sena.labels.noResult') }}</span>
        </template>

        <template slot="status" slot-scope="props">
          <va-select
            :options="statusOptions"
            :value="statusOption(props.rowData.status)"
            textBy="name"
            keyBy="id"
            no-clear
            @input="opt => opt && changeStatus(props.rowData, opt.id)"
          />
        </template>

        <template slot="actions" slot-scope="props">
          <va-popover :message="`${$t('sena.tooltips.result')}`" placement="bottom">
            <va-button
              flat
              color="gray"
              icon="fa fa-futbol-o"
              href="#s-result"
              @click="openResult(props.rowData)"
            />
          </va-popover>
          <va-popover v-if="isDraft" :message="`${$t('sena.tooltips.removeGame')}`" placement="bottom">
            <va-button
              flat
              color="gray"
              icon="fa fa-trash"
              @click="askRemove(props.rowData)"
            />
          </va-popover>
        </template>
      </va-data-table>

      <p class="sena-hint mt-2" v-if="!isDraft">
        {{ $t('sena.hints.gamesetFrozen') }}
      </p>
    </va-card>

    <va-modal
      v-model="modalRemove"
      size="small"
      :title="$t('modals.titleDelete')"
      :message="$t('sena.modals.removeGame')"
      :okText="$t('modals.confirm')"
      :cancelText="$t('modals.cancel')"
      @ok="removeGame"
    />

    <va-modal
      v-model="modalStatus"
      size="small"
      :title="$t('sena.modals.statusTitle')"
      :message="statusWarning"
      :okText="$t('modals.confirm')"
      :cancelText="$t('modals.cancel')"
      @ok="applyStatus"
      @cancel="pendingStatus = null"
    />
  </div>
</template>

<script>
import gql from 'graphql-tag'
import GamesResultsAddUpdate from '../games/GamesResultsAddUpdate'
import { senaErrorMixin } from './senaErrors'

const EDITION_GAMES = gql`
  query SenaEditionGames($edition: Int!) {
    sena_edition_by_pk(id: $edition) {
      id
      name
      edition_number
      status
      lockout_at
    }
    progress: sena_edition_progress(where: {sena_edition_id: {_eq: $edition}}) {
      games_total
      games_resolved
      games_blocked
      tickets_total
      tickets_alive
    }
    games: sena_edition_game(where: {sena_edition_id: {_eq: $edition}}, order_by: {order_index: asc}) {
      id
      order_index
      status
      status_note
      game_id
      game {
        id
        datetime
        club1 { id name }
        club2 { id name }
        championship { id name }
        results {
          id
          pts_club1
          pts_club2
        }
      }
    }
  }
`

export default {
  name: 'SenaEditionGames',
  mixins: [senaErrorMixin],
  components: { GamesResultsAddUpdate },
  props: {
    edition: { required: true },
  },
  data () {
    return {
      sena_edition_by_pk: null,
      progressRows: [],
      games: [],
      loading: false,
      showResult: false,
      resultData: null,
      modalRemove: false,
      removeTarget: null,
      modalStatus: false,
      pendingStatus: null,
    }
  },
  apollo: {
    editionData: {
      query: EDITION_GAMES,
      variables () {
        return { edition: Number(this.edition) }
      },
      manual: true,
      result ({ data, loading }) {
        this.loading = loading
        if (loading || !data) return
        this.sena_edition_by_pk = data.sena_edition_by_pk
        this.progressRows = data.progress || []
        this.games = data.games || []
      },
    },
  },
  computed: {
    progress () {
      return this.progressRows.length ? this.progressRows[0] : null
    },
    isDraft () {
      return this.sena_edition_by_pk && this.sena_edition_by_pk.status === 'DRAFT'
    },
    cardTitle () {
      const e = this.sena_edition_by_pk
      return e ? `${this.$t('sena.cards.games')} — ${e.name}` : this.$t('sena.cards.games')
    },
    lockoutText () {
      const e = this.sena_edition_by_pk
      return e && e.lockout_at
        ? this.$moment(e.lockout_at).format('DD/MM/YYYY [às] HH:mm')
        : this.$t('sena.labels.lockoutPending')
    },
    statusOptions () {
      return ['SCHEDULED', 'PLAYED', 'CANCELED', 'POSTPONED'].map(v => ({
        id: v,
        name: this.$t('sena.gameStatus.' + v),
      }))
    },
    statusWarning () {
      if (!this.pendingStatus) return ''
      const blocking = ['CANCELED', 'POSTPONED'].includes(this.pendingStatus.status)
      return blocking
        ? this.$t('sena.modals.statusVoids')
        : this.$t('sena.modals.statusPlain')
    },
    fields () {
      const moment = this.$moment
      return [
        { name: 'order_index', title: '#', width: '50px', dataClass: 'text-center' },
        { name: '__slot:confronto', title: this.$t('sena.headings.match') },
        {
          name: 'game',
          title: this.$t('sena.headings.datetime'),
          width: '170px',
          callback (value) {
            return moment(value.datetime).format('DD/MM/YYYY [às] HH:mm')
          },
        },
        {
          name: '__slot:placar',
          title: this.$t('sena.headings.score'),
          width: '90px',
          dataClass: 'text-center',
          titleClass: 'text-center',
        },
        {
          name: '__slot:status',
          title: this.$t('sena.headings.gameStatus'),
          width: '190px',
        },
        {
          name: '__slot:actions',
          title: this.$t('tables.actions'),
          dataClass: 'text-center',
          titleClass: 'text-center',
        },
      ]
    },
  },
  methods: {
    resultOf (row) {
      const r = row.game && row.game.results
      return r && r.length ? r[0] : null
    },
    statusOption (value) {
      return this.statusOptions.find(o => o.id === value) || null
    },
    openResult (row) {
      // GamesResultsAddUpdate espera o objeto `game` cru (com club1/club2).
      this.resultData = row.game
      this.showResult = true
    },
    closeResult (v) {
      this.showResult = v
      this.resultData = null
    },
    refetchAll () {
      // Cada placar salvo dispara t_result_sena_score no banco, que reavalia
      // os bilhetes. Refetch para o cabeçalho mostrar o novo nº de sobreviventes.
      this.$apollo.queries.editionData.refetch()
    },
    changeStatus (row, status) {
      if (row.status === status) return
      this.pendingStatus = { row, status }
      this.modalStatus = true
    },
    applyStatus () {
      const { row, status } = this.pendingStatus
      this.$apollo
        .mutate({
          mutation: gql`
            mutation SetGameStatus($id: Int!, $status: sena_game_status!) {
              update_sena_edition_game_by_pk(pk_columns: {id: $id}, _set: {status: $status}) {
                id
                status
              }
            }
          `,
          variables: { id: row.id, status },
        })
        .then(() => {
          this.pendingStatus = null
          this.senaToastSuccess('toasts.successUpdate')
          this.refetchAll()
        })
        .catch(error => {
          this.pendingStatus = null
          this.senaToastError(error, 'toasts.errorUpdate')
        })
    },
    askRemove (row) {
      this.removeTarget = row
      this.modalRemove = true
    },
    removeGame () {
      this.$apollo
        .mutate({
          mutation: gql`
            mutation RemoveGame($id: Int!) {
              delete_sena_edition_game_by_pk(id: $id) {
                id
              }
            }
          `,
          variables: { id: this.removeTarget.id },
        })
        .then(() => {
          this.senaToastSuccess('toasts.successDelete')
          this.refetchAll()
        })
        .catch(error => this.senaToastError(error, 'toasts.errorDelete'))
    },
  },
}
</script>

<style lang="scss">
.sena-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 0.75rem 1rem;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 4px;

  &__item {
    white-space: nowrap;
  }
}

.sena-muted {
  color: #999999;
}

.sena-hint {
  font-size: $font-size-mini;
  color: #888888;
}
</style>
